// Core packages
import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";

// Third party packages
import { MatDialog } from "@angular/material";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import { finalize, take } from "rxjs/operators";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { PermissionsService } from "src/app/modules/permissions/permissions.service";
import { RolesService } from "src/app/modules/roles/roles.service";
import { ConfirmDialogComponent } from "src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component";
import { Role } from "src/app/shared/models/role/role.model";
import { IUser } from "src/app/shared/models/user/user.interface";
import { DateTimeFormatPipe } from "src/app/shared/pipes/date-format.pipe";
import { ApiService } from "src/app/shared/services/api.service";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";
import { ThemeService } from "src/app/shared/services/theme.service";

@Component({
  selector: "app-role-detail",
  templateUrl: "./role-detail.page.html",
  styleUrls: ["./role-detail.page.scss"],
})
export class RoleDetailComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  role: Role;

  // BEGIN - Edit role form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly
  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.save"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true,
    },
  };
  deleteBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.delete"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "warn",
    spinnerColor: "warn",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    customClass: "",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-trash",
      inline: true,
    },
  };
  // END - Edit role form

  dateTimeFormat: DateTimeFormatPipe;
  detailCardIsLoading = true;
  deleteCardIsLoading = true;

  /**
   * Class constructor
   */
  constructor(
    private configService: ConfigService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private toastrService: ToastrService,
    private helperService: HelperService,
    private translateService: TranslateService,
    private themeService: ThemeService,
    private authService: AuthService,
    public dialog: MatDialog,
    private cookieService: CookieService,
    private rolesService: RolesService,
    private permissionsService: PermissionsService,
    private apiService: ApiService // DO NOT MOVE since it's used for 'autocomplete' field type
  ) {}

  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Set page title
    this.themeService.setPageTitle(
      this.translateService.instant("roles.detail.pageTitle")
    );

    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe((res) => (this.loggedUser = res))
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        (res) => (this.loggedUserPermissions = res)
      )
    );

    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;

    // Get requested role data
    const roleId = this.activatedRoute.snapshot.params.roleId;
    this.subscriptions.push(
      this.rolesService.get(roleId).subscribe(
        (res) => {
          if (res.status) {
            this.role = res.data;

            // Stop loading in delete form
            this.deleteCardIsLoading = false;

            this.getPermissionsAndInitForm();

            // Make detail form card visible by disabling his card-spinner
            this.detailCardIsLoading = false;
            return;
          }
          // Something bad happened
          // redirect user to home
          this.toastrService.error(
            this.translateService.instant("common.errors.unknown")
          );
          this.router.navigate([this.lang, "app", "roles", "list"]);
        },
        (err) => {
          // if we came here users with given userId do not exist
          this.helperService.handleError(err);
          this.router.navigate([this.lang, "errors", "404"]);
        }
      )
    );
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Preload role's permissions data and then init form. If something
   * goes wrong just init the form
   *
   * @since 1.0.0
   */
  getPermissionsAndInitForm(): void {
    this.subscriptions.push(
      this.permissionsService
        .getList(0, 10, null, null, {
          ids: this.role.permissions.map((perm) => perm.permissionId),
        })
        .subscribe(
          (res) => {
            if (res.status) {
              this.initForm(res.data.items);
            } else {
              this.initForm();
            }
          },
          (err) => {
            this.helperService.handleError(err);
            this.initForm();
          }
        )
    );
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(availablePermissions?: any[]): void {
    // Set model
    this.model = this.role;

    // Get only permission ids
    this.role.permissions.forEach((perm, index) => {
      this.model.permissions[index] = perm.permissionId;
    });

    // Set fields
    this.fields = [
      ...this.fields,
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12 col-md-6",
            type: "input",
            key: "name",
            templateOptions: {
              label: this.translateService.instant("roles.fields.name"),
              placeholder: this.translateService.instant("roles.fields.name"),
              required: true,
              minLength: 3,
              maxLength: 50,
            },
          },
          {
            className: "col-12 col-md-6",
            type: "select",
            key: "type",
            templateOptions: {
              label: this.translateService.instant("roles.fields.type"),
              placeholder: this.translateService.instant("roles.fields.type"),
              required: true,
              options: this.configService.availableRoleTypes,
            },
          },
          {
            className: "col-12",
            type: "autocomplete",
            key: "permissions",
            templateOptions: {
              label: this.translateService.instant("roles.fields.permissions"),
              placeholder: this.translateService.instant(
                "roles.fields.permissions"
              ),
              required: true,
              bindLabel: "key",
              bindValue: "_id",
              multiple: true,
              search$: this.permissionsService.getList.bind(this),
              initialValues: availablePermissions,
              hideSelected: true,
              typeToSearchText: this.translateService.instant(
                "common.words.typeToSearch"
              ),
              clearAllText: this.translateService.instant(
                "common.words.clearAll"
              ),
              notFoundText: this.translateService.instant(
                "common.words.notFound"
              ),
              loadingText: this.translateService.instant(
                "common.words.loading"
              ),
            },
          },
          {
            className: "col-12",
            type: "textarea",
            key: "description",
            templateOptions: {
              label: this.translateService.instant("roles.fields.description"),
              placeholder: this.translateService.instant(
                "roles.fields.description"
              ),
              maxLength: 500,
              required: true,
              rows: 2,
            },
            expressionProperties: {
              "templateOptions.description":
                '(model[field.key]||"").length+"/" +field.templateOptions.maxLength',
            },
          },
        ],
      },
    ];
  }

  /**
   * Handle form submit
   *
   * @since 1.0.0
   */
  onFormSubmit(model: any): void {
    // Prevent invalid form submit
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.controls[key].markAsTouched();
      });
      console.warn("form", this.form);
      return;
    }

    // Activate submit button loading status
    this.formBtnOptions.active = true;

    this.subscriptions.push(
      this.rolesService
        .update(this.role._id, model)
        .pipe(finalize(() => (this.formBtnOptions.active = false)))
        .subscribe(
          (res) => {
            if (res.status) {
              this.toastrService.success(res.message);
            }
          },
          (err) => {
            // Choose one of the following error handling method.
            // 1) First one show a message right under the form fields
            //    (if the form is properly setted)
            // 2) Second one is used to show a mat-error under each field
            //    in the dynamic form (is meant to be used only in dynamicForm)
            // 3) Third one show toastr notifications for each error
            // 4) Fourth one is used to show mat-error under each field
            //    in the ngx-formly form (is mean to be used only with ngx-formly)
            // this.helperService.handleFormError(form, err);
            // this.helperService.handleDynamicFormError(form, err);
            // this.helperService.handleError(err);
            this.helperService.handleFormlyError(this.form, err);
          }
        )
    );
  }

  /**
   * Remove current permission from database
   *
   * @since 1.0.0
   */
  onDelete(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant("dialogs.confirm.title"),
        message: this.translateService.instant("dialogs.confirm.message"),
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.undo"),
      },
      width: "500px",
    });
    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        this.subscriptions.push(
          this.rolesService
            .delete(this.role._id)
            .pipe(finalize(() => (this.deleteBtnOptions.active = false)))
            .subscribe(
              (res2) => {
                this.toastrService.success(res2.message);
                // Once the user is deleted, redirect to list
                this.router.navigate([this.lang, "app", "roles", "list"]);
                return;
              },
              (err) => {
                // Choose one of the following error handling
                // method. The first one shows a message right
                // under the form fields (if the form is properly
                // setted), the second one shows toastr
                // notifications for each error
                // this.helperService.handleFormError(this.passwordForm, err);
                this.helperService.handleError(err);
              }
            )
        );
      });
  }
}
